/* FFL Room Visualizer V2 — manual perspective overlay (no external AI).
   Author: 2026-06-01
   Approach: user uploads a room photo, drags 4 corner handles to mark the floor
   quadrilateral, picks a flooring product, and the canvas renders the texture
   tiled + perspective-warped into that quadrilateral via homography
   (forward + inverse 3x3) and per-pixel sampling.
*/
(function () {
  'use strict';

  // ---------- math: 3x3 matrices + homography from 4 point pairs ----------
  // Solves for the 3x3 projective transform H such that H * src_i = dst_i (homogeneous).
  // Reference: standard DLT for a 4-point homography.
  function solveHomography(src, dst) {
    // Build the 8x8 linear system A*h = b for h = [h00..h21] (h22 = 1).
    var A = [];
    var b = [];
    for (var i = 0; i < 4; i++) {
      var sx = src[i][0], sy = src[i][1];
      var dx = dst[i][0], dy = dst[i][1];
      A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy]);
      b.push(dx);
      A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy]);
      b.push(dy);
    }
    var h = gaussSolve(A, b);
    if (!h) return null;
    return [
      [h[0], h[1], h[2]],
      [h[3], h[4], h[5]],
      [h[6], h[7], 1]
    ];
  }
  function gaussSolve(A, b) {
    var n = b.length;
    var M = A.map(function (row, i) { return row.concat([b[i]]); });
    for (var col = 0; col < n; col++) {
      // pivot
      var piv = col;
      for (var r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
      if (Math.abs(M[piv][col]) < 1e-12) return null;
      var tmp = M[col]; M[col] = M[piv]; M[piv] = tmp;
      // eliminate
      for (var r2 = 0; r2 < n; r2++) {
        if (r2 === col) continue;
        var f = M[r2][col] / M[col][col];
        for (var c2 = col; c2 <= n; c2++) M[r2][c2] -= f * M[col][c2];
      }
    }
    var x = new Array(n);
    for (var i = 0; i < n; i++) x[i] = M[i][n] / M[i][i];
    return x;
  }
  function applyH(H, x, y) {
    var X = H[0][0] * x + H[0][1] * y + H[0][2];
    var Y = H[1][0] * x + H[1][1] * y + H[1][2];
    var W = H[2][0] * x + H[2][1] * y + H[2][2];
    return [X / W, Y / W];
  }

  // ---------- core visualizer instance ----------
  function init(root) {
    if (!root) return;
    var fileInput = root.querySelector('[data-fflv-file]');
    var uploadBtn = root.querySelector('[data-fflv-upload]');
    var canvas = root.querySelector('[data-fflv-canvas]');
    var handlesLayer = root.querySelector('[data-fflv-handles]');
    var stage = root.querySelector('[data-fflv-stage]');
    var emptyMsg = root.querySelector('[data-fflv-empty]');
    var resultPane = root.querySelector('[data-fflv-result]');
    var beforeBtn = root.querySelector('[data-fflv-before]');
    var afterBtn = root.querySelector('[data-fflv-after]');
    var resetBtn = root.querySelector('[data-fflv-reset]');
    var downloadBtn = root.querySelector('[data-fflv-download]');
    var brightness = root.querySelector('[data-fflv-brightness]');
    var scale = root.querySelector('[data-fflv-scale]');
    var selectedLabel = root.querySelector('[data-fflv-selected]');
    var search = root.querySelector('[data-fflv-search]');
    var grid = root.querySelector('[data-fflv-grid]');

    if (!canvas || !fileInput) return;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });

    var roomImg = null;          // <img> of the uploaded room
    var textureImg = null;       // <img> of the selected product
    var selectedProductTitle = '';
    var corners = null;          // 4 corners (canvas coords)
    var draggingIdx = -1;
    var canvasW = 800, canvasH = 600;
    var mode = 'after';          // 'before' | 'after'
    var lastComposite = null;    // ImageData of the rendered composite
    var pixelRatio = window.devicePixelRatio || 1;

    function setStageVisible(yes) {
      if (yes) {
        emptyMsg && (emptyMsg.style.display = 'none');
        stage.style.display = 'block';
        resultPane && (resultPane.style.display = 'flex');
      } else {
        emptyMsg && (emptyMsg.style.display = 'flex');
        stage.style.display = 'none';
        resultPane && (resultPane.style.display = 'none');
      }
    }

    function fitCanvas() {
      if (!roomImg) return;
      var maxW = stage.clientWidth || 800;
      var ratio = roomImg.naturalHeight / roomImg.naturalWidth;
      canvasW = Math.min(maxW, 1100);
      canvasH = Math.round(canvasW * ratio);
      canvas.style.width = canvasW + 'px';
      canvas.style.height = canvasH + 'px';
      canvas.width = Math.round(canvasW * pixelRatio);
      canvas.height = Math.round(canvasH * pixelRatio);
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      handlesLayer.style.width = canvasW + 'px';
      handlesLayer.style.height = canvasH + 'px';
    }

    function defaultCorners() {
      // Floor quad: roughly the lower-half trapezoid (perspective default)
      var topY = Math.round(canvasH * 0.58);
      var botY = Math.round(canvasH * 0.96);
      var leftTop = Math.round(canvasW * 0.32);
      var rightTop = Math.round(canvasW * 0.68);
      var leftBot = Math.round(canvasW * 0.04);
      var rightBot = Math.round(canvasW * 0.96);
      return [
        { x: leftTop, y: topY },     // 0 top-left
        { x: rightTop, y: topY },    // 1 top-right
        { x: rightBot, y: botY },    // 2 bottom-right
        { x: leftBot, y: botY }      // 3 bottom-left
      ];
    }

    function renderHandles() {
      handlesLayer.innerHTML = '';
      if (!corners) return;
      corners.forEach(function (c, i) {
        var el = document.createElement('div');
        el.className = 'fflv__handle';
        el.style.left = c.x + 'px';
        el.style.top = c.y + 'px';
        el.dataset.idx = String(i);
        el.title = 'Drag to adjust floor corner';
        handlesLayer.appendChild(el);
      });
      // edges
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'fflv__edges');
      svg.setAttribute('width', String(canvasW));
      svg.setAttribute('height', String(canvasH));
      var pts = corners.map(function (c) { return c.x + ',' + c.y; }).join(' ');
      var poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      poly.setAttribute('points', pts);
      poly.setAttribute('fill', 'none');
      poly.setAttribute('stroke', '#C8102E');
      poly.setAttribute('stroke-width', '2');
      poly.setAttribute('stroke-dasharray', '5 5');
      svg.appendChild(poly);
      handlesLayer.appendChild(svg);
    }

    // Drag handlers
    function attachHandleDrag() {
      handlesLayer.addEventListener('pointerdown', function (e) {
        var t = e.target;
        if (!t.classList || !t.classList.contains('fflv__handle')) return;
        e.preventDefault();
        draggingIdx = parseInt(t.dataset.idx, 10);
        t.setPointerCapture(e.pointerId);
      });
      handlesLayer.addEventListener('pointermove', function (e) {
        if (draggingIdx < 0 || !corners) return;
        var rect = handlesLayer.getBoundingClientRect();
        var x = Math.max(0, Math.min(canvasW, e.clientX - rect.left));
        var y = Math.max(0, Math.min(canvasH, e.clientY - rect.top));
        corners[draggingIdx].x = x;
        corners[draggingIdx].y = y;
        renderHandles();
        scheduleRender();
      });
      handlesLayer.addEventListener('pointerup', function () { draggingIdx = -1; });
      handlesLayer.addEventListener('pointercancel', function () { draggingIdx = -1; });
    }

    var renderTimer = null;
    function scheduleRender() {
      if (renderTimer) cancelAnimationFrame(renderTimer);
      renderTimer = requestAnimationFrame(render);
    }

    // ---------- main render ----------
    function render() {
      renderTimer = null;
      if (!roomImg) return;

      // 1. Draw the room photo as background
      ctx.clearRect(0, 0, canvasW, canvasH);
      ctx.drawImage(roomImg, 0, 0, canvasW, canvasH);

      if (mode === 'before' || !textureImg || !corners) {
        lastComposite = null;
        return;
      }

      // 2. Build the texture-source tile (we draw the texture tiled into a
      // scratch canvas at the texture size × repeat, then sample via inverse
      // homography for each pixel inside the floor quadrilateral).
      var tileScale = parseFloat(scale && scale.value || '1.0');
      var bright = parseFloat(brightness && brightness.value || '1.0');

      // Texture tile: scale texture so that across the quadrilateral roughly
      // tileScale boards span the longer edge. Compute the average top width.
      var quadW = Math.hypot(corners[1].x - corners[0].x, corners[1].y - corners[0].y);
      var quadH = Math.hypot(corners[3].x - corners[0].x, corners[3].y - corners[0].y);
      var dstW = Math.max(8, Math.round(quadW));
      var dstH = Math.max(8, Math.round(quadH));

      // The source rectangle that the homography maps FROM is the texture's
      // own pixel space, tiled to dstW × dstH. We render an offscreen tiled
      // texture canvas, then sample it via the homography.
      var tileCanvas = document.createElement('canvas');
      tileCanvas.width = dstW;
      tileCanvas.height = dstH;
      var tctx = tileCanvas.getContext('2d');
      var tw = Math.max(48, Math.round(textureImg.naturalWidth * tileScale * (dstW / 800)));
      var th = Math.max(48, Math.round(textureImg.naturalHeight * tileScale * (dstW / 800)));
      // Draw the texture tiled
      for (var ty = 0; ty < dstH; ty += th) {
        for (var tx = 0; tx < dstW; tx += tw) {
          tctx.drawImage(textureImg, tx, ty, tw, th);
        }
      }

      // 3. Compute INVERSE homography mapping destination quad → source rectangle.
      // Forward H sends [0,0]..[dstW,dstH] (source rect) to corners (dst quad).
      // We need the inverse: for each pixel in the destination quad, find the
      // matching pixel in the tile.
      var src = [[0, 0], [dstW, 0], [dstW, dstH], [0, dstH]];
      var dst = corners.map(function (c) { return [c.x, c.y]; });
      // Solve homography from dst→src (so we can iterate over dst pixels)
      var Hinv = solveHomography(dst, src);
      if (!Hinv) return;

      // 4. Find bounding box of the quad in canvas coords.
      var minX = Math.max(0, Math.floor(Math.min(dst[0][0], dst[1][0], dst[2][0], dst[3][0])));
      var maxX = Math.min(canvasW, Math.ceil(Math.max(dst[0][0], dst[1][0], dst[2][0], dst[3][0])));
      var minY = Math.max(0, Math.floor(Math.min(dst[0][1], dst[1][1], dst[2][1], dst[3][1])));
      var maxY = Math.min(canvasH, Math.ceil(Math.max(dst[0][1], dst[1][1], dst[2][1], dst[3][1])));

      var tileData = tctx.getImageData(0, 0, dstW, dstH);
      var roomData = ctx.getImageData(minX * pixelRatio, minY * pixelRatio, (maxX - minX) * pixelRatio, (maxY - minY) * pixelRatio);
      // (We re-fetch a 1:1 imageData by drawing the room into a temp canvas at canvas units)
      var tmp = document.createElement('canvas');
      tmp.width = canvasW; tmp.height = canvasH;
      var tmpctx = tmp.getContext('2d');
      tmpctx.drawImage(roomImg, 0, 0, canvasW, canvasH);
      var roomData1 = tmpctx.getImageData(minX, minY, maxX - minX, maxY - minY);

      var pix = roomData1.data;

      // helper: is point inside convex quad? Use sign-of-cross test for each edge.
      function inside(px, py) {
        var sgn = 0;
        for (var i = 0; i < 4; i++) {
          var ax = dst[i][0], ay = dst[i][1];
          var bx = dst[(i + 1) % 4][0], by = dst[(i + 1) % 4][1];
          var cross = (bx - ax) * (py - ay) - (by - ay) * (px - ax);
          if (cross === 0) continue;
          if (sgn === 0) sgn = cross > 0 ? 1 : -1;
          else if ((cross > 0 ? 1 : -1) !== sgn) return false;
        }
        return true;
      }

      // 5. For each pixel inside the quad, sample the tile via Hinv and blend.
      for (var y = minY; y < maxY; y++) {
        for (var x = minX; x < maxX; x++) {
          if (!inside(x + 0.5, y + 0.5)) continue;
          var s = applyH(Hinv, x + 0.5, y + 0.5);
          var sx = Math.floor(s[0]);
          var sy = Math.floor(s[1]);
          if (sx < 0 || sx >= dstW || sy < 0 || sy >= dstH) continue;
          var ti = (sy * dstW + sx) * 4;
          var pi = ((y - minY) * (maxX - minX) + (x - minX)) * 4;
          // Blend with original room: lower alpha to keep room lighting cues
          var ta = 0.92; // texture opacity
          var br = tileData.data[ti] * bright;
          var bg = tileData.data[ti + 1] * bright;
          var bb = tileData.data[ti + 2] * bright;
          if (br > 255) br = 255; if (bg > 255) bg = 255; if (bb > 255) bb = 255;
          pix[pi] = br * ta + pix[pi] * (1 - ta);
          pix[pi + 1] = bg * ta + pix[pi + 1] * (1 - ta);
          pix[pi + 2] = bb * ta + pix[pi + 2] * (1 - ta);
        }
      }

      // 6. Put back the modified bounding box
      tmpctx.putImageData(roomData1, minX, minY);
      ctx.drawImage(tmp, 0, 0, canvasW, canvasH);
      lastComposite = tmp;
    }

    // ---------- file input ----------
    if (uploadBtn) uploadBtn.addEventListener('click', function () { fileInput.click(); });
    fileInput.addEventListener('change', function () {
      var f = fileInput.files && fileInput.files[0];
      if (!f) return;
      var r = new FileReader();
      r.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          roomImg = img;
          setStageVisible(true);
          fitCanvas();
          corners = defaultCorners();
          renderHandles();
          scheduleRender();
        };
        img.src = e.target.result;
      };
      r.readAsDataURL(f);
    });
    // Drag-and-drop support on the empty area
    if (emptyMsg) {
      emptyMsg.addEventListener('dragover', function (e) { e.preventDefault(); emptyMsg.classList.add('is-drag'); });
      emptyMsg.addEventListener('dragleave', function () { emptyMsg.classList.remove('is-drag'); });
      emptyMsg.addEventListener('drop', function (e) {
        e.preventDefault();
        emptyMsg.classList.remove('is-drag');
        var f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (!f) return;
        fileInput.files = e.dataTransfer.files;
        fileInput.dispatchEvent(new Event('change'));
      });
    }

    // ---------- product selection ----------
    if (grid) {
      grid.addEventListener('click', function (e) {
        var a = e.target.closest('[data-fflv-product]');
        if (!a) return;
        e.preventDefault();
        var url = a.getAttribute('data-img');
        var title = a.getAttribute('data-title') || '';
        if (!url) return;
        // Mark active card
        var prev = grid.querySelector('.is-active');
        if (prev) prev.classList.remove('is-active');
        a.classList.add('is-active');
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          textureImg = img;
          selectedProductTitle = title;
          if (selectedLabel) selectedLabel.textContent = title;
          if (roomImg) {
            mode = 'after';
            if (beforeBtn) beforeBtn.classList.remove('is-active');
            if (afterBtn) afterBtn.classList.add('is-active');
            scheduleRender();
          }
        };
        img.onerror = function () {
          alert('Could not load that texture. Please try another product.');
        };
        img.src = url;
      });
    }
    // Search filter
    if (search && grid) {
      search.addEventListener('input', function () {
        var q = (search.value || '').toLowerCase().trim();
        var cards = grid.querySelectorAll('[data-fflv-product]');
        cards.forEach(function (c) {
          var name = (c.getAttribute('data-title') || '').toLowerCase();
          c.style.display = (q === '' || name.indexOf(q) !== -1) ? '' : 'none';
        });
      });
    }

    // ---------- controls ----------
    if (beforeBtn) beforeBtn.addEventListener('click', function () { mode = 'before'; beforeBtn.classList.add('is-active'); afterBtn && afterBtn.classList.remove('is-active'); scheduleRender(); });
    if (afterBtn) afterBtn.addEventListener('click', function () { mode = 'after'; afterBtn.classList.add('is-active'); beforeBtn && beforeBtn.classList.remove('is-active'); scheduleRender(); });
    if (resetBtn) resetBtn.addEventListener('click', function () {
      if (!roomImg) return;
      corners = defaultCorners();
      renderHandles();
      scheduleRender();
    });
    if (brightness) brightness.addEventListener('input', scheduleRender);
    if (scale) scale.addEventListener('input', scheduleRender);
    if (downloadBtn) downloadBtn.addEventListener('click', function () {
      if (!roomImg) return;
      var link = document.createElement('a');
      link.download = 'ffl-room-visualizer.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    });

    // ---------- init ----------
    attachHandleDrag();
    setStageVisible(false);
    window.addEventListener('resize', function () {
      if (!roomImg) return;
      fitCanvas();
      // Re-scale corners proportionally on viewport change
      var ratioW = canvasW / (corners[2].x || 1);
      // Conservative: just reset corners to default on resize
      corners = defaultCorners();
      renderHandles();
      scheduleRender();
    });
  }

  function boot() {
    document.querySelectorAll('[data-ffl-visualizer]').forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
